import React, { useEffect, useState } from 'react';
import { req } from '../services/api';
import Modal from '../components/Modal';

const CATEGORIES = [
  'STANDARD',
  'BUSINESS',
  'VIP',
  'SUV',
  'FAMILY',
  'LIMO'
];

const CATEGORY_LABELS = {
  STANDARD: 'Standard',
  BUSINESS: 'Business',
  VIP: 'VIP',
  SUV: 'SUV',
  FAMILY: 'Family',
  LIMO: 'Limuzína'
};

const normalize = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const formatDate = value => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('cs-CZ');
};

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [detailModal, setDetailModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [photosModal, setPhotosModal] = useState(false);

  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [currentDocs, setCurrentDocs] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPhotos, setEditPhotos] = useState({});
  const [editPhotosDirty, setEditPhotosDirty] = useState(false);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    setLoading(true);

    try {
      const data = await req('GET', '/vehicles');

      if (Array.isArray(data)) {
        setVehicles(data);
      }
    } catch (error) {
      console.error('Vehicles load failed:', error);
      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed vozidel: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    } finally {
      setLoading(false);
    }
  };



  const ownerName = vehicle => {
    if (vehicle.ownerType === 'FLEET') {
      return vehicle.fleet?.companyName || 'Flotila';
    }

    if (vehicle.ownerType === 'DRIVER') {
      return vehicle.driver?.user?.name || 'Řidič';
    }

    if (vehicle.ownerType === 'INVALID_BOTH') {
      return 'Chybný vlastník';
    }

    return 'Bez vlastníka';
  };

  const ownerSecondary = vehicle => {
    if (vehicle.ownerType === 'FLEET') {
      return vehicle.fleet?.user?.email || '';
    }

    if (vehicle.ownerType === 'DRIVER') {
      return vehicle.driver?.user?.email || '';
    }

    return '';
  };

  const q = normalize(search);

  const filtered = vehicles.filter(vehicle => {
    const matchesSearch =
      normalize(vehicle.brand).includes(q) ||
      normalize(vehicle.plate).includes(q) ||
      normalize(vehicle.color).includes(q) ||
      normalize(vehicle.docNumber).includes(q) ||
      normalize(ownerName(vehicle)).includes(q) ||
      normalize(ownerSecondary(vehicle)).includes(q);

    const matchesOwner =
      !ownerFilter ||
      vehicle.ownerType === ownerFilter;

    const matchesApproval =
      !approvalFilter ||
      (approvalFilter === 'approved' && vehicle.approved) ||
      (approvalFilter === 'pending' && !vehicle.approved);

    const matchesCategory =
      !categoryFilter ||
      vehicle.category === categoryFilter;

    return (
      matchesSearch &&
      matchesOwner &&
      matchesApproval &&
      matchesCategory
    );
  });

  const pendingCount = vehicles.filter(
    vehicle => !vehicle.approved
  ).length;

  const fleetCount = vehicles.filter(
    vehicle => vehicle.ownerType === 'FLEET'
  ).length;

  const driverCount = vehicles.filter(
    vehicle => vehicle.ownerType === 'DRIVER'
  ).length;

  const activeCount = vehicles.filter(
    vehicle => vehicle.isActiveVehicle
  ).length;

  const approve = async id => {
    try {
      await req('PATCH', '/vehicles/' + id, {
        approved: true
      });

      await loadVehicles();
    } catch (error) {
      alert(
        'Chyba p\u0159i schvalov\u00e1n\u00ed vozidla: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const reject = async id => {
    if (
      !window.confirm(
        'Opravdu chcete zru\u0161it schv\u00e1len\u00ed tohoto vozidla?'
      )
    ) {
      return;
    }

    try {
      await req('PATCH', '/vehicles/' + id, {
        approved: false
      });

      await loadVehicles();
    } catch (error) {
      alert(
        'Chyba p\u0159i zru\u0161en\u00ed schv\u00e1len\u00ed vozidla: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const deleteVehicle = async vehicle => {
    const label = [vehicle.brand, vehicle.plate].filter(Boolean).join(' ');

    if (
      !window.confirm(
        'Opravdu chcete odstranit vozidlo ' +
          label +
          ' z provozu? Historie j\u00edzd a souvisej\u00edc\u00ed z\u00e1znamy z\u016fstanou zachov\u00e1ny.'
      )
    ) {
      return;
    }

    try {
      await req('DELETE', '/vehicles/' + vehicle.id);

      if (currentVehicle?.id === vehicle.id) {
        setDetailModal(false);
        setCurrentVehicle(null);
      }

      await loadVehicles();
      alert('Vozidlo bylo odstran\u011bno z provozu.');
    } catch (error) {
      alert(
        error?.message ||
          'Vozidlo se nepoda\u0159ilo odstranit z provozu.'
      );
    }
  };

  const parsePhotos = value => {
    if (!value) return {};

    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return Object.fromEntries(
          parsed.map((uri, index) => [
            'Fotka ' + (index + 1),
            uri
          ])
        );
      }

      if (
        parsed &&
        typeof parsed === 'object'
      ) {
        return parsed;
      }
    } catch {}

    return {};
  };

  const openDetail = vehicle => {
    setCurrentVehicle(vehicle);
    setDetailModal(true);
  };

  const openEdit = async vehicle => {
    setCurrentVehicle(vehicle);
    setEditPhotos({});
    setEditPhotosDirty(false);

    try {
      const docs = await req(
        'GET',
        '/vehicles/' + vehicle.id + '/photos'
      );

      setEditPhotos(parsePhotos(docs?.photos));

      setEditForm({
        brand: vehicle.brand || '',
        year: vehicle.year || '',
        color: vehicle.color || '',
        plate: vehicle.plate || '',
        seats: vehicle.seats || '',
        category: vehicle.category || 'STANDARD',
        docNumber: vehicle.docNumber || ''
      });

      setEditModal(true);
    } catch (error) {
      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed vozidla: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const deletePhoto = key => {
    const updated = { ...editPhotos };
    delete updated[key];
    setEditPhotos(updated);
    setEditPhotosDirty(true);
  };

  const saveEdit = async () => {
    const payload = {
      ...editForm
    };

    if (editPhotosDirty) {
      payload.photos = editPhotos;
    }

    try {
      await req(
        'PATCH',
        '/vehicles/' + currentVehicle.id + '/edit',
        payload
      );

      setEditModal(false);
      await loadVehicles();

      alert('\u2713 Vozidlo bylo upraveno');
    } catch (error) {
      alert(
        'Chyba p\u0159i \u00faprav\u011b vozidla: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const openPhotos = async vehicle => {
    setCurrentVehicle(vehicle);
    setCurrentDocs(null);
    setPhotosModal(true);

    try {
      const docs = await req(
        'GET',
        '/vehicles/' + vehicle.id + '/photos'
      );

      setCurrentDocs(docs);
    } catch (error) {
      setPhotosModal(false);
      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed fotografi\u00ed vozidla: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const openDocument = value => {
    if (!value) return;

    const w = window.open();

    if (!w) {
      alert(
        'Prohlížeč zablokoval otevření dokumentu.'
      );
      return;
    }

    if (
      typeof value === 'string' &&
      value.startsWith('data:application/pdf')
    ) {
      w.document.write(
        '<iframe src="' +
          value +
          '" style="position:fixed;inset:0;width:100%;height:100%;border:0;"></iframe>'
      );
    } else {
      w.document.write(
        '<body style="margin:0;background:#111;text-align:center;">' +
          '<img src="' +
          value +
          '" style="max-width:100%;max-height:100vh;" />' +
          '</body>'
      );
    }
  };

  const openTechCert = async vehicle => {
    try {
      const docs = await req(
        'GET',
        '/vehicles/' + vehicle.id + '/technical-certificate'
      );

      if (!docs?.technicalCertificate) {
        alert(
          'Technick\u00fd pr\u016fkaz nen\u00ed u vozidla nahr\u00e1n.'
        );
        return;
      }

      openDocument(docs.technicalCertificate);
    } catch (error) {
      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed technick\u00e9ho pr\u016fkazu: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const resetFilters = () => {
    setSearch('');
    setOwnerFilter('');
    setApprovalFilter('');
    setCategoryFilter('');
  };

  return (
    <div>
      <div className="page-title">Vozidla</div>

      <div className="page-sub">
        Vozidla samostatných řidičů a flotil, dokumentace a schvalování
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">
            {vehicles.length}
          </div>
          <div className="stat-label">
            Celkem vozidel
          </div>
        </div>

        <div
          className={
            pendingCount > 0
              ? 'stat-card alert'
              : 'stat-card'
          }
        >
          <div className="stat-value">
            {pendingCount}
          </div>
          <div className="stat-label">
            Čeká na schválení
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {driverCount}
          </div>
          <div className="stat-label">
            Vozidla řidičů
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {fleetCount}
          </div>
          <div className="stat-label">
            Vozidla flotil
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {activeCount}
          </div>
          <div className="stat-label">
            Aktivní vozidla
          </div>
        </div>
      </div>

      <div className="admin-toolbar">
        <input
          className="search-bar"
          placeholder="Hledat vozidlo, SPZ, vlastníka nebo číslo dokladu..."
          value={search}
          onChange={event =>
            setSearch(event.target.value)
          }
        />

        <select
          value={ownerFilter}
          onChange={event =>
            setOwnerFilter(event.target.value)
          }
        >
          <option value="">
            Všichni vlastníci
          </option>
          <option value="DRIVER">
            Samostatný řidič
          </option>
          <option value="FLEET">
            Flotila
          </option>
          <option value="UNASSIGNED">
            Bez vlastníka
          </option>
          <option value="INVALID_BOTH">
            Chybná vazba
          </option>
        </select>

        <select
          value={approvalFilter}
          onChange={event =>
            setApprovalFilter(event.target.value)
          }
        >
          <option value="">
            Všechny stavy
          </option>
          <option value="approved">
            Schválená
          </option>
          <option value="pending">
            Čekají na schválení
          </option>
        </select>

        <select
          value={categoryFilter}
          onChange={event =>
            setCategoryFilter(event.target.value)
          }
        >
          <option value="">
            Všechny kategorie
          </option>

          {CATEGORIES.map(category => (
            <option
              key={category}
              value={category}
            >
              {CATEGORY_LABELS[category]}
            </option>
          ))}
        </select>

        <button
          className="btn-sm btn-gray"
          onClick={resetFilters}
        >
          Zrušit filtry
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">
            Přehled vozidel
          </span>

          <span className="dashboard-muted">
            Zobrazeno {filtered.length} z{' '}
            {vehicles.length}
          </span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Vlastník</th>
                <th>Vozidlo</th>
                <th>SPZ</th>
                <th>Kategorie</th>
                <th>Status</th>
                <th>Aktivní</th>
                <th>Využití</th>
                <th>Dokumentace</th>
                <th>Registrace</th>
                <th>Akce</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="10"
                    className="loading-cell"
                  >
                    Načítám...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="empty-cell"
                  >
                    Žádná vozidla odpovídající filtrům
                  </td>
                </tr>
              ) : (
                filtered.map(vehicle => (
                  <tr key={vehicle.id}>
                    <td>
                      <strong>
                        {ownerName(vehicle)}
                      </strong>

                      <br />

                      <span className="table-secondary">
                        {vehicle.ownerType === 'FLEET'
                          ? 'Flotila'
                          : vehicle.ownerType ===
                              'DRIVER'
                            ? 'Samostatný řidič'
                            : vehicle.ownerType ===
                                'INVALID_BOTH'
                              ? 'CHYBA: driver + fleet'
                              : 'Bez vlastníka'}
                      </span>

                      {ownerSecondary(vehicle) && (
                        <>
                          <br />
                          <span className="table-secondary">
                            {ownerSecondary(vehicle)}
                          </span>
                        </>
                      )}
                    </td>

                    <td>
                      <strong>
                        {vehicle.brand || '—'}
                      </strong>

                      <br />

                      <span className="table-secondary">
                        {vehicle.color || '—'}
                        {' · '}
                        {vehicle.year || '—'}
                        {' · '}
                        {vehicle.seats || '—'} míst
                      </span>
                    </td>

                    <td>
                      <strong className="gold-text">
                        {vehicle.plate || '—'}
                      </strong>
                    </td>

                    <td>
                      <span className="badge badge-gold">
                        {CATEGORY_LABELS[
                          vehicle.category
                        ] || vehicle.category}
                      </span>
                    </td>

                    <td>
                      {vehicle.approved ? (
                        <span className="badge badge-green">
                          Schváleno
                        </span>
                      ) : (
                        <span className="badge badge-gold">
                          Čeká
                        </span>
                      )}
                    </td>

                    <td>
                      {vehicle.isActiveVehicle ? (
                        <span className="badge badge-green">
                          Aktivní
                        </span>
                      ) : (
                        <span className="badge badge-gray">
                          Ne
                        </span>
                      )}

                      {vehicle.activeForDriver?.user
                        ?.name && (
                        <>
                          <br />
                          <span className="table-secondary">
                            {
                              vehicle
                                .activeForDriver
                                .user.name
                            }
                          </span>
                        </>
                      )}
                    </td>

                    <td>
                      <span className="table-secondary">
                        Jízdy:{' '}
                        {vehicle._count?.rides || 0}
                      </span>

                      <br />

                      <span className="table-secondary">
                        Nabídky:{' '}
                        {vehicle._count?.bids || 0}
                      </span>
                    </td>

                    <td>
                      <div className="actions">
                        {vehicle.photoCount > 0 && (
                          <button
                            className="btn-sm btn-blue"
                            onClick={() =>
                              openPhotos(vehicle)
                            }
                          >
                            Fotky {vehicle.photoCount}
                          </button>
                        )}

                        {vehicle.hasTechnicalCertificate && (
                          <button
                            className="btn-sm btn-gold"
                            onClick={() =>
                              openTechCert(vehicle)
                            }
                          >
                            TP
                          </button>
                        )}

                        {!vehicle.photoCount &&
                          !vehicle.hasTechnicalCertificate && (
                            <span className="table-secondary">
                              —
                            </span>
                          )}
                      </div>
                    </td>

                    <td className="table-secondary">
                      {formatDate(vehicle.createdAt)}
                    </td>

                    <td>
                      <div className="actions">
                        <button
                          className="btn-sm btn-blue"
                          onClick={() =>
                            openDetail(vehicle)
                          }
                        >
                          Detail
                        </button>

                        <button
                          className="btn-sm btn-gold"
                          onClick={() =>
                            openEdit(vehicle)
                          }
                        >
                          Upravit
                        </button>

                        {!vehicle.approved ? (
                          <button
                            className="btn-sm btn-green"
                            onClick={() =>
                              approve(vehicle.id)
                            }
                          >
                            Schválit
                          </button>
                        ) : (
                          <button
                            className="btn-sm btn-red"
                            onClick={() =>
                              reject(vehicle.id)
                            }
                          >
                            Zrušit schválení
                          </button>
                        )}

                        <button
                          className="btn-sm btn-red"
                          onClick={() => deleteVehicle(vehicle)}
                        >
                          Smazat
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailModal && currentVehicle && (
        <Modal
          title={
            'Detail vozidla ' +
            (currentVehicle.plate || '')
          }
          onClose={() =>
            setDetailModal(false)
          }
        >
          <div className="payout-detail-box">
            <div className="payout-row">
              <span className="payout-row-label">
                Vlastník
              </span>
              <span className="payout-row-value">
                {ownerName(currentVehicle)}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Typ vlastníka
              </span>
              <span className="payout-row-value">
                {currentVehicle.ownerType ===
                'FLEET'
                  ? 'Flotila'
                  : currentVehicle.ownerType ===
                      'DRIVER'
                    ? 'Samostatný řidič'
                    : currentVehicle.ownerType ===
                        'INVALID_BOTH'
                      ? 'CHYBA – dva vlastníci'
                      : 'Bez vlastníka'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Značka
              </span>
              <span className="payout-row-value">
                {currentVehicle.brand || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                SPZ
              </span>
              <span className="payout-row-value gold-text">
                {currentVehicle.plate || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Rok
              </span>
              <span className="payout-row-value">
                {currentVehicle.year || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Barva
              </span>
              <span className="payout-row-value">
                {currentVehicle.color || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Počet míst
              </span>
              <span className="payout-row-value">
                {currentVehicle.seats || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Kategorie
              </span>
              <span className="payout-row-value">
                {CATEGORY_LABELS[
                  currentVehicle.category
                ] || currentVehicle.category}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Číslo dokladu
              </span>
              <span className="payout-row-value">
                {currentVehicle.docNumber || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Schválení
              </span>
              <span className="payout-row-value">
                {currentVehicle.approved
                  ? 'Schváleno'
                  : 'Čeká na schválení'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Aktivní vozidlo řidiče
              </span>
              <span className="payout-row-value">
                {currentVehicle.isActiveVehicle
                  ? currentVehicle.activeForDriver
                      ?.user?.name || 'Ano'
                  : 'Ne'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Jízdy
              </span>
              <span className="payout-row-value">
                {currentVehicle._count?.rides || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Nabídky
              </span>
              <span className="payout-row-value">
                {currentVehicle._count?.bids || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Fotografie
              </span>
              <span className="payout-row-value">
                {currentVehicle.photoCount || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Technický průkaz
              </span>
              <span className="payout-row-value">
                {currentVehicle.hasTechnicalCertificate
                  ? 'Nahrán'
                  : 'Nenahrán'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Registrace
              </span>
              <span className="payout-row-value">
                {formatDate(
                  currentVehicle.createdAt
                )}
              </span>
            </div>
          </div>

          <div className="actions">
            {currentVehicle.photoCount > 0 && (
              <button
                className="btn-sm btn-blue"
                onClick={() => {
                  setDetailModal(false);
                  openPhotos(currentVehicle);
                }}
              >
                Fotografie
              </button>
            )}

            {currentVehicle.hasTechnicalCertificate && (
              <button
                className="btn-sm btn-gold"
                onClick={() =>
                  openTechCert(currentVehicle)
                }
              >
                Technický průkaz
              </button>
            )}

            <button
              className="btn-sm btn-gold"
              onClick={() => {
                setDetailModal(false);
                openEdit(currentVehicle);
              }}
            >
              Upravit
            </button>

            {!currentVehicle.approved ? (
              <button
                className="btn-sm btn-green"
                onClick={async () => {
                  await approve(currentVehicle.id);
                  setDetailModal(false);
                }}
              >
                Schválit
              </button>
            ) : (
              <button
                className="btn-sm btn-red"
                onClick={async () => {
                  await reject(currentVehicle.id);
                  setDetailModal(false);
                }}
              >
                Zrušit schválení
              </button>
            )}

            <button
              className="btn-sm btn-red"
              onClick={() => deleteVehicle(currentVehicle)}
            >
              Smazat
            </button>
          </div>
        </Modal>
      )}

      {editModal && currentVehicle && (
        <Modal
          title="Editace vozidla"
          onClose={() => setEditModal(false)}
          actions={
            <>
              <button
                className="btn-gray"
                onClick={() =>
                  setEditModal(false)
                }
              >
                Zrušit
              </button>

              <button
                className="btn"
                onClick={saveEdit}
              >
                Uložit
              </button>
            </>
          }
        >
          <div className="edit-row">
            <label className="edit-label">
              Značka
            </label>

            <input
              value={editForm.brand}
              onChange={event =>
                setEditForm({
                  ...editForm,
                  brand: event.target.value
                })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">
              Rok
            </label>

            <input
              type="number"
              value={editForm.year}
              onChange={event =>
                setEditForm({
                  ...editForm,
                  year: event.target.value
                })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">
              Barva
            </label>

            <input
              value={editForm.color}
              onChange={event =>
                setEditForm({
                  ...editForm,
                  color: event.target.value
                })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">
              SPZ
            </label>

            <input
              value={editForm.plate}
              onChange={event =>
                setEditForm({
                  ...editForm,
                  plate: event.target.value
                })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">
              Počet míst
            </label>

            <input
              type="number"
              value={editForm.seats}
              onChange={event =>
                setEditForm({
                  ...editForm,
                  seats: event.target.value
                })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">
              Kategorie
            </label>

            <select
              value={editForm.category}
              onChange={event =>
                setEditForm({
                  ...editForm,
                  category: event.target.value
                })
              }
            >
              {CATEGORIES.map(category => (
                <option
                  key={category}
                  value={category}
                >
                  {CATEGORY_LABELS[category]}
                </option>
              ))}
            </select>
          </div>

          <div className="edit-row">
            <label className="edit-label">
              Číslo dokladu
            </label>

            <input
              value={editForm.docNumber}
              onChange={event =>
                setEditForm({
                  ...editForm,
                  docNumber: event.target.value
                })
              }
            />
          </div>

          {Object.keys(editPhotos).length > 0 && (
            <div className="edit-row">
              <label className="edit-label">
                Fotografie
              </label>

              <div className="photo-grid">
                {Object.entries(
                  editPhotos
                ).map(([key, uri]) => (
                  <div
                    key={key}
                    className="photo-item"
                  >
                    <img
                      src={uri}
                      alt={key}
                    />

                    <button
                      type="button"
                      className="photo-delete"
                      onClick={() =>
                        deletePhoto(key)
                      }
                    >
                      ×
                    </button>

                    <div className="photo-label">
                      {key}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}

      {photosModal && currentVehicle && (
        <Modal
          title={
            'Fotografie vozidla ' +
            currentVehicle.plate
          }
          onClose={() =>
            setPhotosModal(false)
          }
        >
          {!currentDocs ? (
            <div className="loading-cell">
              Načítám...
            </div>
          ) : (
            (() => {
              const photos = parsePhotos(
                currentDocs.photos
              );

              if (
                Object.keys(photos).length === 0
              ) {
                return (
                  <div className="empty-cell">
                    Žádné fotografie
                  </div>
                );
              }

              return (
                <div className="photo-grid">
                  {Object.entries(photos).map(
                    ([key, uri]) => (
                      <div
                        key={key}
                        style={{
                          textAlign: 'center'
                        }}
                      >
                        <img
                          src={uri}
                          alt={key}
                          style={{
                            width: '100%',
                            maxWidth: 300,
                            height: 200,
                            objectFit: 'cover',
                            borderRadius: 10,
                            marginBottom: 6
                          }}
                        />

                        <div className="table-secondary">
                          {key}
                        </div>
                      </div>
                    )
                  )}
                </div>
              );
            })()
          )}
        </Modal>
      )}
    </div>
  );
}
